/**
 * AssistantWidget: floating AI assistant (FAB + chat drawer).
 *
 * Rendered once inside AppLayout so it's available on every authenticated page.
 * - FAB: fixed bottom-right, opens the chat
 * - Chat: slide-up panel with message history and input
 * - On submit: calls /profiles/nl-search, navigates to /profiles/all, stores filters in AssistantContext
 */

import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import SmartToyRounded from '@mui/icons-material/SmartToyRounded';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { type AssistantMessage, useAssistant } from '@/contexts/AssistantContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/shared/api/api-client';
import { paths } from '@/shared/routes/paths';
import type { FilterGroup } from '@/types/filters';

import { assistantCountPath } from './assistant-count-logic';

interface NlSearchResponse {
  filters: FilterGroup;
  interpretation: string;
}

const HWNI_PATH = paths.profiles.all;

export default function AssistantWidget() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { open, setOpen, toggleOpen, messages, addMessage, setPendingFilters } = useAssistant();
  const { user } = useAuth();
  const licenceId = user?.licence_id;

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    const trimmed = query.trim();
    if (trimmed.length < 3 || loading) return;

    addMessage({ role: 'user', content: trimmed });
    setQuery('');
    setLoading(true);

    try {
      const result = await api.post<NlSearchResponse>('/profiles/nl-search', { query: trimmed });

      // Store filters in context for the Profiles page to consume
      setPendingFilters(result.filters);

      // Navigate to HWNI if not already there
      if (locationRef.current.pathname !== HWNI_PATH) {
        navigate(HWNI_PATH);
      }

      // Count with the request the HNWIs table sends for these filters (#697),
      // so the figure announced is the table total; none without a licence.
      const countPath = assistantCountPath(result.filters, licenceId);
      const total = countPath
        ? ((await api.getRaw<unknown[]>(countPath)).pagination?.total ?? 0)
        : undefined;

      addMessage({
        role: 'assistant',
        content: `Search applied: **${result.interpretation}**`,
        meta: {
          resultCount: total,
          interpretation: result.interpretation,
        },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Search failed';
      addMessage({ role: 'assistant', content: `Sorry, something went wrong: ${msg}` });
    } finally {
      setLoading(false);
    }
  }, [query, loading, addMessage, setPendingFilters, navigate, licenceId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <>
      {/* ─── Chat panel ─── */}
      <Grow
        in={open}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 250, exit: 200 }}
        style={{ transformOrigin: 'bottom right' }}
      >
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 0,
            width: 400,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: (t) => t.zIndex.modal,
            borderRadius: '12px 0 0 12px',
            overflow: 'hidden',
            bgcolor: isDark ? alpha('#0F1420', 0.97) : alpha('#FFFFFF', 0.98),
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            boxShadow: isDark
              ? `0 16px 48px ${alpha('#000', 0.5)}, 0 0 0 1px ${alpha('#00E0DA', 0.08)}`
              : `0 16px 48px ${alpha('#000', 0.12)}, 0 0 0 1px ${alpha('#00E0DA', 0.1)}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              background: isDark
                ? `linear-gradient(135deg, ${alpha('#00E0DA', 0.06)} 0%, transparent 100%)`
                : `linear-gradient(135deg, ${alpha('#00E0DA', 0.05)} 0%, transparent 100%)`,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, #00E0DA 0%, ${alpha('#00E0DA', 0.7)} 100%)`,
                boxShadow: `0 2px 8px ${alpha('#00E0DA', 0.3)}`,
              }}
            >
              <AutoAwesomeRounded sx={{ fontSize: 18, color: isDark ? '#080C14' : '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.3 }}
              >
                Invyo AI
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                Profile Search Assistant
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              scrollbarWidth: 'thin',
            }}
          >
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                  Analyzing your request…
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{
              px: 1.5,
              py: 1.5,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: isDark ? alpha('#080C14', 0.4) : alpha('#F4F6FA', 0.5),
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder="Describe who you're looking for…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Search" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleSubmit}
                            disabled={query.trim().length < 3 || loading}
                            sx={{
                              color: query.trim().length >= 3 ? 'primary.main' : 'text.disabled',
                              '&:hover': { color: 'primary.dark' },
                            }}
                          >
                            <SendRounded sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontSize: '0.8125rem',
                  bgcolor: isDark ? alpha('#131926', 0.8) : alpha('#fff', 0.9),
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 2px ${alpha('#00E0DA', 0.12)}`,
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Grow>

      {/* ─── FAB ─── */}
      <Tooltip title={open ? '' : 'AI Assistant'} arrow placement="left">
        <Fab
          onClick={toggleOpen}
          aria-label="AI Assistant"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 0,
            zIndex: (t) => t.zIndex.modal + 1,
            width: 44,
            height: 44,
            minHeight: 'unset',
            borderRadius: '12px 0 0 12px',
            background: open
              ? isDark
                ? alpha('#131926', 0.95)
                : alpha('#E8EDF5', 0.95)
              : `linear-gradient(135deg, #00E0DA 0%, ${alpha('#00E0DA', 0.8)} 100%)`,
            color: open ? 'text.secondary' : isDark ? '#080C14' : '#fff',
            boxShadow: open
              ? `0 4px 16px ${alpha('#000', 0.2)}`
              : `-4px 0 20px ${alpha('#00E0DA', 0.25)}, 0 4px 12px ${alpha('#000', 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: open
                ? isDark
                  ? alpha('#1E2536', 0.95)
                  : alpha('#D8DEE8', 0.95)
                : `linear-gradient(135deg, #33E8E3 0%, #00E0DA 100%)`,
              width: 50,
              boxShadow: open
                ? `0 6px 24px ${alpha('#000', 0.25)}`
                : `-6px 0 28px ${alpha('#00E0DA', 0.35)}, 0 4px 16px ${alpha('#000', 0.15)}`,
            },
            '&:active': {
              width: 42,
            },
          }}
        >
          {open ? (
            <CloseRounded sx={{ fontSize: 20 }} />
          ) : (
            <SmartToyRounded sx={{ fontSize: 20 }} />
          )}
        </Fab>
      </Tooltip>
    </>
  );
}

function MessageBubble({ message }: { message: AssistantMessage }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          px: 1.75,
          py: 1,
          borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          bgcolor: isUser
            ? isDark
              ? `linear-gradient(135deg, ${alpha('#00E0DA', 0.15)} 0%, ${alpha('#00E0DA', 0.08)} 100%)`
              : alpha('#00E0DA', 0.1)
            : isDark
              ? alpha('#1E2536', 0.7)
              : alpha('#F4F6FA', 0.9),
          background: isUser
            ? `linear-gradient(135deg, ${alpha('#00E0DA', 0.15)} 0%, ${alpha('#00E0DA', 0.08)} 100%)`
            : undefined,
          border: `1px solid ${
            isUser ? alpha('#00E0DA', isDark ? 0.12 : 0.15) : alpha(theme.palette.divider, 0.08)
          }`,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.78rem',
            lineHeight: 1.55,
            color: 'text.primary',
            '& strong': { fontWeight: 600 },
          }}
          dangerouslySetInnerHTML={{
            __html: message.content
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .replace(/\n/g, '<br/>'),
          }}
        />

        {/* Result count badge */}
        {message.meta?.resultCount !== undefined && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 1,
              pt: 0.75,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            <SearchRounded sx={{ fontSize: 13, color: 'primary.main' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                fontSize: '0.72rem',
              }}
            >
              {message.meta.resultCount} profile{message.meta.resultCount !== 1 ? 's' : ''} found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
